import { orgChartClient } from 'myNet'

import orgChartDB from 'libs/DB/orgChartDB'

type ListenerFunction = (data: OrgChartData | null) => void

class ListenerObj {
  listeners: ListenerFunction[]

  constructor() {
    this.listeners = []
  }

  notify = (data: OrgChartData | null): void => {
    this.listeners.forEach((listener) => {
      listener(data)
    })
  }

  add = (listener: ListenerFunction): void => {
    if (this.listeners.indexOf(listener) < 0) {
      this.listeners.push(listener)
    }
  }

  remove = (listener: ListenerFunction): void => {
    const pos = this.listeners.indexOf(listener)
    if (pos >= 0) {
      this.listeners.splice(pos, 1)
    }
  }
}

interface DepartmentMember {
  user_id: string
  jobtitle_id: string
}

interface Department {
  id: string
  title: string
  name?: string
  position?: string
  members: DepartmentMember[]
}

interface JobPosition {
  id: string
  title: string
  name?: string
}

interface JobTitle {
  id: string
  title: string
  name?: string
  position?: string
  jobposition_id: string
}

interface Manager {
  id: string
  name: string
}

interface EmailItem {
  address: string
}

interface UserData {
  id?: string
  account?: string
  name?: string
  display_name?: string
  emails?: EmailItem[]
  profile_photo_url?: string
  organization_name?: string
  jobposition?: string
  mobiles?: any[]
  departmentData?: {
    id?: string
    title?: string
    name?: string
    position?: string
  }
  jobData?: {
    jobposition?: string
    jobtitle?: string
    position?: string
    jobposition_id?: string | null
  }
}

interface User {
  id: string
  data: UserData
}

interface OrgChartData {
  departments: Department[]
  jobpositions: JobPosition[]
  jobtitles: JobTitle[]
  managers: Manager[]
  users: User[]
}

interface DBResponse {
  data: any
}

interface AllDataResponse {
  dataList: User[]
}

interface SubData {
  department?: Department
  job?: JobTitle
  jobposition?: JobPosition
}

interface ApiResponse {
  origin?: {
    status: number
    headers: Headers
  }
  data?: {
    data?: OrgChartData
  }
}

class OrgChartHelper {
  isLoaded: boolean
  changeListener: ListenerObj
  private lastNotifiedETag: string | null | undefined = undefined

  constructor() {
    this.isLoaded = false
    this.changeListener = new ListenerObj()
  }

  // IndexedDB 초기화를 보장한다. loadData/startLoading 이전에 호출해야 캐시 읽기가
  // isActive=false로 조용히 null을 반환하는 것을 막는다.
  ensureDB = async (): Promise<boolean> => {
    if (this.isLoaded === false) {
      this.isLoaded = await orgChartDB.getDB()
    }
    return this.isLoaded
  }

  startLoading = async (): Promise<OrgChartData | null> => {
    // 명시적 리로드 진입점이므로 다음 loadData()에서 반드시 notify가 발화되도록 강제한다.
    // 이 장치가 없으면 findContact → loadData 경로가 먼저 lastNotifiedETag를 세팅한 뒤
    // Tree 리마운트/refresh 시 같은 eTag 비교로 notify가 억제되어 로딩 스피너가 잔류한다.
    this.lastNotifiedETag = undefined

    let ret: OrgChartData | null = null
    await this.ensureDB()
    if (this.isLoaded === true) {
      try {
        const eTagInfo = await orgChartDB.getData('info_etag')
        console.log('eTagInfo : ', eTagInfo)
        ret = await this.loadNewData(eTagInfo?.data)
      } catch (e) {
        console.error('orgChartDB.getData error : ', e)
        ret = await this.loadNewData()
      }
    }

    return ret
  }

  saveData = async (data: OrgChartData | null, eTag?: string): Promise<OrgChartData | null> => {
    await orgChartDB.clear()

    if (eTag != null) {
      await orgChartDB.addData('info_etag', eTag)
    }

    if (data != null) {
      const { departments, jobpositions, jobtitles, managers, users } = data

      await orgChartDB.addData('info_departments', departments)
      await orgChartDB.addData('info_jobpositions', jobpositions)
      await orgChartDB.addData('info_jobtitles', jobtitles)
      await orgChartDB.addData('info_managers', managers)

      // forEach + async는 저장 완료를 기다리지 않아, loadData()가 사용자 저장이 끝나기 전
      // 실행될 수 있다(IndexedDB 트랜잭션 직렬화에 암묵 의존). Promise.allSettled로 완료를
      // 명시적으로 보장하되, 개별 저장 실패가 전체 실패로 번지지 않도록 한다(부분 실패 허용).
      // putData(objectStore.put)는 중복 id/재시도에 멱등이라 add의 ConstraintError를 피한다.
      const results = await Promise.allSettled(
        users.map((user) => orgChartDB.putData(user.id, user)),
      )
      const failedCount = results.filter((result) => result.status === 'rejected').length
      if (failedCount > 0) {
        console.warn(`OrgChart saveData: ${failedCount}/${users.length}명 사용자 저장 실패`)
      }

      const ret = await this.loadData()

      return ret
    }

    return null
  }

  loadData = async (options?: { force?: boolean }): Promise<OrgChartData | null> => {
    try {
      const departmentsInfo = await orgChartDB.getData('info_departments')
      const jobpositionsInfo = await orgChartDB.getData('info_jobpositions')
      const jobtitlesInfo = await orgChartDB.getData('info_jobtitles')
      const managersInfo = await orgChartDB.getData('info_managers')
      const eTagInfo = await orgChartDB.getData('info_etag')

      const allData: AllDataResponse | null = await orgChartDB.getAllData()

      if (allData != null) {
        const users = allData.dataList

        const orgChartData: OrgChartData = {
          departments: departmentsInfo?.data ?? [],
          jobpositions: jobpositionsInfo?.data ?? [],
          jobtitles: jobtitlesInfo?.data ?? [],
          managers: managersInfo?.data ?? [],
          users,
        }

        // eTag가 바뀐 경우(최초 포함)에만 리스너를 notify.
        // 이 필터링이 없으면 findContact → loadData 경로에서 매번 notify가 발생해
        // knownSenderCache가 자기파괴 사이클에 빠진다.
        //
        // force=true(캐시 우선 표시 경로)는 eTag 억제를 우회해 즉시 화면에 표시하되,
        // 실데이터(users>0)가 있을 때만 notify한다. 빈 캐시(최초 로그인 등)는 억제하여
        // EmptyData 깜빡임을 막고 스피너를 유지한다. force는 Tree.reload만 사용하므로
        // findContact 경로의 자기파괴 방지 로직에는 영향이 없다.
        const currentETag = (eTagInfo?.data ?? null) as string | null
        const shouldNotify =
          options?.force === true ? users.length > 0 : currentETag !== this.lastNotifiedETag
        if (shouldNotify) {
          this.lastNotifiedETag = currentETag
          this.changeListener.notify(orgChartData)
        }

        return orgChartData
      }
    } catch (e) {
      console.log('OrgChartHelper.loadData error : ', e)
    }
    return null
  }

  getAvatarUrl = (originData: User | UserData): string | null => {
    const data = 'data' in originData ? originData.data : originData
    const { profile_photo_url: profilePhotoUrl } = data

    let imgSrc: string | null = null
    if (
      profilePhotoUrl == null ||
      profilePhotoUrl.trim().length === 0 ||
      profilePhotoUrl.indexOf('my_default') > -1
    ) {
      imgSrc = null
    } else {
      imgSrc = profilePhotoUrl
    }
    return imgSrc
  }

  getAvatarUrlByEmail = async (email: string): Promise<string | null> => {
    const userData = await this.findContact(email)
    let url: string | null = null
    if (userData != null) {
      url = this.getAvatarUrl(userData)
    }
    return url
  }

  // 부서 멤버(user_id)는 존재하는데 사용자 레코드가 비어있는 캐시 불일치를 감지한다.
  // 부분 저장 실패, 외부(DevTools) clear, 과거 경합 등으로 user 레코드만 사라지면
  // departments는 남아 있어 "부서명만 표시 + user not found"가 된다.
  private hasMembersButNoUsers = (data: OrgChartData): boolean => {
    if (data.users != null && data.users.length > 0) {
      return false
    }
    const memberCount = (data.departments ?? []).reduce(
      (sum, department) => sum + (department.members != null ? department.members.length : 0),
      0,
    )
    return memberCount > 0
  }

  loadNewData = async (eTag?: string, retriedOnMismatch = false): Promise<OrgChartData | null> => {
    let ret: OrgChartData | null = null

    try {
      const headers: Record<string, string> = {}

      if (eTag != null) {
        headers['If-None-Match'] = eTag
      }

      const res: ApiResponse = await orgChartClient.get('/org_chart/get_org_chart', {}, { headers })
      console.log('get_org_chart : ', res)
      const { origin, data: resData } = res

      if (origin != null) {
        const { data } = resData || {}
        if (origin.status === 200) {
          const { headers: resHeader } = origin
          const resETag = resHeader.get('Etag')
          console.log('eTag : ', resETag)
          ret = await this.saveData(data || null, resETag || undefined)
        } else if (origin.status === 304) {
          ret = await this.loadData()
          // 304는 saveData를 거치지 않아 깨진 캐시(부서만 있고 사용자 없음)가 자가 회복되지
          // 않는다. 불일치를 감지하면 eTag 없이 1회 강제 재요청(200)해 캐시를 재구축한다.
          if (!retriedOnMismatch && ret != null && this.hasMembersButNoUsers(ret)) {
            console.warn(
              'OrgChart: 캐시 불일치 감지(부서 멤버는 있으나 사용자 없음) → eTag 무시하고 강제 재로드',
            )
            ret = await this.loadNewData(undefined, true)
          }
        }
      }
    } catch (e) {
      console.error('loadNewData error : ', e)
      ret = null
    }

    return ret
  }

  findContact = async (email: string, forceReload?: boolean): Promise<UserData | null> => {
    let allData: OrgChartData | null = null
    if (forceReload === true) {
      allData = await this.startLoading()
    } else {
      allData = await this.loadData()
    }

    if (allData != null) {
      const { users } = allData
      const userDBData = users.find((dbData) => {
        const { account, emails } = dbData.data
        if (account != null && account === email) {
          return true
        }
        if (emails != null && emails.length > 0) {
          return emails.find((emailItem) => emailItem.address === email)
        }
        return false
      })
      if (userDBData != null) {
        return userDBData.data
      }
    }

    return null
  }

  getSubData = async (userId: string): Promise<SubData> => {
    const departments: DBResponse | null = await orgChartDB.getData('info_departments')
    if (departments == null) return {}

    let member: DepartmentMember | undefined = undefined
    const department = departments.data.find((department: Department) => {
      member = department.members.find((user) => user.user_id === userId)
      return member != null
    })
    if (department == null) {
      return {}
    }

    const jobs: DBResponse | null = await orgChartDB.getData('info_jobtitles')
    if (jobs == null) return { department }
    const job = jobs.data.find((job: JobTitle) => job.id === member?.jobtitle_id)

    const jobpositions: DBResponse | null = await orgChartDB.getData('info_jobpositions')
    const jobposition = jobpositions?.data?.find(
      (jobposition: JobPosition) => jobposition.id === job?.jobposition_id,
    )

    return {
      department,
      job,
      jobposition,
    }
  }
}

const helper = new OrgChartHelper()

export default helper
